import React from 'react';
import { Link, graphql } from 'gatsby';
import Img from 'gatsby-image';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { Layout } from '@components';
import styled from 'styled-components';
import { theme, mixins, Main, media } from '@styles';
const { colors, fontSizes, fonts } = theme;

const Hero = styled.header`
  position: relative;
  z-index: 0;
  text-align: center;
  .background {
    top: 0;
    left: 0;
    z-index: -1;
    height: 100%;
    opacity: 0.8;
    object-fit: cover;
  }
  h1 {
    position: absolute;
    width: 100%;
    top: 150px;
    ${media.tablet`top: 90px;`};
    ${media.phablet`top: 50px;`};
    ${media.phone`top: 40px;`};
    z-index: 1;
    color: ${colors.lightestSlate};
  }
  .subtitle {
    position: absolute;
    width: 100%;
    top: 240px;
    ${media.tablet`top: 150px;`};
    ${media.phablet`display: none`};
    ${media.phone`display:none`};
    z-index: 1;
  }
`;

const StyledMainContainer = styled(Main)`
  & > header {
    text-align: center;
    margin-bottom: 100px;
    ${media.phablet`margin-bottom: 0px;`};
    a {
      &:hover,
      &:focus {
        cursor: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='48' viewport='0 0 100 100' style='fill:black;font-size:24px;'><text y='50%'>⚡</text></svg>")
            20 0,
          auto;
      }
    }
  }

  footer {
    ${mixins.flexBetween};
    padding: 1rem 1rem;
    width: 100%;
  }
`;
const StyledGrid = styled.div`
  margin-top: 50px;

  .posts {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    grid-gap: 15px;
    position: relative;
    ${media.desktop`grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));`};
  }
`;
const StyledPostInner = styled.div`
  ${mixins.boxShadow};
  ${mixins.flexBetween};
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  padding: 0rem 0rem;
  height: 100%;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  background-color: ${colors.lightestSlate};
  header,
  a {
    padding: 1rem 1rem;
    width: 100%;
  }
  .cover-image {
    max-height: 230px;
    min-height: 230px;
    width: 100% !important;
  }
`;
const StyledPost = styled.div`
  transition: ${theme.transition};
  cursor: default;
  &:hover,
  &:focus {
    outline: 0;
    ${StyledPostInner} {
      transform: translateY(-5px);
    }
  }
`;

const StyledPostName = styled.h5`
  margin: 0 0 10px;
  font-size: ${fontSizes.xxl};
  color: ${colors.lightNavy};
`;
const StyledPostDescription = styled.div`
  font-size: 17px;
  color: ${colors.lightNavy};
`;
const StyledDate = styled.span`
  text-transform: uppercase;
  font-family: ${fonts.SFMono};
  font-size: ${fontSizes.xs};
  color: ${colors.lightNavy};
`;
const StyledTags = styled.ul`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  padding: 0;
  margin: 0;
  list-style: none;

  li {
    font-family: ${fonts.SFMono};
    font-size: ${fontSizes.xs};
    line-height: 1.75;
    margin-right: 15px;
    &:last-of-type {
      margin-right: 0;
    }
    a {
      ${mixins.inlineLink};
      color: ${colors.navy};
      &:hover,
      &:focus {
        color: ${colors.lightNavy};
      }
    }
  }
`;

const BlogPage = ({ data, location }) => {
  const posts = data.allStrapiArticle.edges;
  const hero = data.blogHero.edges[0].node.frontmatter;
  return (
    <Layout location={location}>
      <Helmet>
        <title>Blog | Jane Li</title>
        <link rel="canonical" href="https://trust-janeli.netlify.app/blog" />
      </Helmet>

      <StyledMainContainer>
        <Hero>
          <Img fluid={hero.cover.childImageSharp.fluid} alt={hero.title} className="background" />
          <h1 className="big-title">{hero.title}</h1>
          <p className="subtitle">by {hero.name}</p>
          {/* <img src={herobackground} alt="background" className="background" /> */}
        </Hero>
        <StyledGrid>
          <div className="posts">
            {posts.length > 0 &&
              posts.map(({ node }) => {
                const { strapiId, title, description, image, published_at, category } = node;
                return (
                  <StyledPost key={strapiId} tabIndex="0">
                    <StyledPostInner>
                      <img src={image.publicURL} alt="background" className="cover-image" />
                      <header>
                        <Link to={`blog/${strapiId}`}>
                          <StyledPostName>{title}</StyledPostName>
                          <StyledPostDescription>{description}</StyledPostDescription>
                        </Link>
                      </header>
                      <footer>
                        <StyledDate>{published_at}</StyledDate>
                        <StyledTags>
                          <li>
                            <Link to={`/`}> #{category.name}</Link>
                          </li>
                        </StyledTags>
                      </footer>
                    </StyledPostInner>
                  </StyledPost>
                );
              })}
          </div>
        </StyledGrid>
      </StyledMainContainer>
    </Layout>
  );
};

BlogPage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};

export default BlogPage;

export const pageQuery = graphql`
  query contentArcticles {
    blogHero: allMarkdownRemark(filter: { fileAbsolutePath: { regex: "/blog/" } }) {
      edges {
        node {
          frontmatter {
            title
            cover {
              childImageSharp {
                fluid(maxWidth: 1600, quality: 90, traceSVG: { color: "#0b1930" }) {
                  ...GatsbyImageSharpFluid_withWebp_tracedSVG
                }
              }
            }
            name
          }
        }
      }
    }
    allStrapiArticle(sort: { order: DESC, fields: [published_at] }) {
      edges {
        node {
          strapiId
          published_at
          strapiId
          title
          description
          content
          updated_at
          image {
            publicURL
            childImageSharp {
              fluid(maxWidth: 600) {
                ...GatsbyImageSharpFluid
              }
              fixed(width: 600, height: 400) {
                ...GatsbyImageSharpFixed
              }
            }
          }
          category {
            id
            name
          }
        }
      }
    }
  }
`;
